/**
 * QuestionsStep Component
 *
 * Phase 8.1: Step 3 of the v2 assessment wizard
 * Shows 10-13 questions grouped by category (compliance, efficiency, safety)
 */

import { useMemo } from 'react';
import {
  ClipboardCheck,
  Gauge,
  Shield,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { QuestionCard } from './QuestionCard';
import type { RubricOption, SectionQuestion, QuestionCategory } from '@guild/shared';

interface QuestionResponse {
  score: number | null;
  feedback: string;
}

interface QuestionsStepProps {
  questions: SectionQuestion[];
  rubricOptions: RubricOption[];
  responses: Record<string, QuestionResponse>;
  onResponseChange: (questionId: string, response: QuestionResponse) => void;
  sectionTitle?: string;
  isLoading?: boolean;
}

// Category configuration
const categoryConfig: Record<
  QuestionCategory,
  { label: string; icon: typeof ClipboardCheck; color: string; bgColor: string }
> = {
  compliance: {
    label: 'Compliance',
    icon: ClipboardCheck,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 border-emerald-200',
  },
  efficiency: {
    label: 'Efficiency',
    icon: Gauge,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 border-blue-200',
  },
  safety: {
    label: 'Safety',
    icon: Shield,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50 border-orange-200',
  },
};

export function QuestionsStep({
  questions,
  rubricOptions,
  responses,
  onResponseChange,
  sectionTitle,
  isLoading = false,
}: QuestionsStepProps) {
  // Group questions by category
  const groupedQuestions = useMemo(() => {
    const groups: Record<QuestionCategory, SectionQuestion[]> = {
      compliance: [],
      efficiency: [],
      safety: [],
    };

    for (const question of questions) {
      const category = question.category as QuestionCategory;
      if (groups[category]) {
        groups[category].push(question);
      } else {
        // Default to compliance if unknown category
        groups.compliance.push(question);
      }
    }

    return groups;
  }, [questions]);

  // Calculate progress and scores
  const stats = useMemo(() => {
    const totalQuestions = questions.length;
    const answeredQuestions = Object.values(responses).filter((r) => r != null && r.score != null).length;
    const progress = totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;

    // Calculate category scores
    const categoryScores: Record<QuestionCategory, { total: number; count: number; avg: number }> = {
      compliance: { total: 0, count: 0, avg: 0 },
      efficiency: { total: 0, count: 0, avg: 0 },
      safety: { total: 0, count: 0, avg: 0 },
    };

    for (const question of questions) {
      const response = responses[question.id];
      // Check response exists AND has a non-null score
      if (response != null && response.score != null) {
        const category = question.category as QuestionCategory;
        if (categoryScores[category]) {
          categoryScores[category].total += response.score;
          categoryScores[category].count += 1;
        }
      }
    }

    // Calculate averages
    for (const category of Object.keys(categoryScores) as QuestionCategory[]) {
      const { total, count } = categoryScores[category];
      categoryScores[category].avg = count > 0 ? Math.round(total / count) : 0;
    }

    // Overall score: 40% compliance + 30% efficiency + 30% safety
    const overallScore = Math.round(
      categoryScores.compliance.avg * 0.4 +
        categoryScores.efficiency.avg * 0.3 +
        categoryScores.safety.avg * 0.3
    );

    return { totalQuestions, answeredQuestions, progress, categoryScores, overallScore };
  }, [questions, responses]);

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00674a] mx-auto mb-4" />
        <p className="text-gray-500">Loading questions...</p>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <ClipboardCheck className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Assessment Questions Available
        </h3>
        <p className="text-gray-500 max-w-md mx-auto">
          {sectionTitle ? (
            <>
              The job "<span className="font-medium">{sectionTitle}</span>" doesn't have any
              assessment questions yet. Please ensure the SOP has been processed and questions
              were generated for this job.
            </>
          ) : (
            'No questions found for this job. Please go back and select a different job, or ensure the SOP has been fully processed.'
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Assessment Questions</h2>
        <p className="text-gray-500">
          {sectionTitle && (
            <>
              Assessing: <span className="font-medium text-gray-700">{sectionTitle}</span> —{' '}
            </>
          )}
          Score each question using the 6-point rubric scale.
        </p>
      </div>

      {/* Progress bar */}
      <div className="bg-gray-100 rounded-full h-2 mb-6 overflow-hidden">
        <div
          className="bg-[#00674a] h-full rounded-full transition-all duration-500"
          style={{ width: `${stats.progress}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="flex flex-wrap gap-4 mb-8">
        {/* Progress */}
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
          <CheckCircle2 className="w-4 h-4 text-[#00674a]" />
          <span className="text-sm font-medium text-gray-700">
            {stats.answeredQuestions} / {stats.totalQuestions} answered
          </span>
        </div>

        {/* Category scores */}
        {(Object.keys(categoryConfig) as QuestionCategory[]).map((category) => {
          const config = categoryConfig[category];
          const categoryStats = stats.categoryScores[category];

          if (categoryStats.count === 0) return null;

          return (
            <div
              key={category}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${config.bgColor}`}
            >
              <config.icon className={`w-4 h-4 ${config.color}`} />
              <span className={`text-sm font-medium ${config.color}`}>
                {config.label}: {categoryStats.avg}
              </span>
            </div>
          );
        })}

        {/* Overall score */}
        {stats.answeredQuestions > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-[#00674a] text-white rounded-lg ml-auto">
            <span className="text-sm font-semibold">
              Overall: {stats.overallScore}
            </span>
          </div>
        )}
      </div>

      {/* Questions by category */}
      <div className="space-y-10">
        {(Object.keys(categoryConfig) as QuestionCategory[]).map((category) => {
          const categoryQuestions = groupedQuestions[category];
          if (categoryQuestions.length === 0) return null;

          const config = categoryConfig[category];
          const Icon = config.icon;

          return (
            <div key={category}>
              {/* Category header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.bgColor}`}>
                  <Icon className={`w-5 h-5 ${config.color}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${config.color}`}>
                    {config.label}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {categoryQuestions.length} question{categoryQuestions.length > 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-4 pl-[52px]">
                {categoryQuestions.map((question) => (
                  <QuestionCard
                    key={question.id}
                    question={question}
                    rubricOptions={rubricOptions}
                    score={responses[question.id]?.score ?? null}
                    feedback={responses[question.id]?.feedback ?? ''}
                    onScoreChange={(score) =>
                      onResponseChange(question.id, {
                        score,
                        feedback: responses[question.id]?.feedback ?? '',
                      })
                    }
                    onFeedbackChange={(feedback) =>
                      onResponseChange(question.id, {
                        score: responses[question.id]?.score ?? null,
                        feedback,
                      })
                    }
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default QuestionsStep;
